
import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import { initializeAuth, getAuth, indexedDBLocalPersistence, browserLocalPersistence, browserPopupRedirectResolver, GoogleAuthProvider, EmailAuthProvider, FacebookAuthProvider, GithubAuthProvider, TwitterAuthProvider, OAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

import { firebaseConfig } from './firebase-config';
export { firebaseConfig } from './firebase-config';

// Initialize Firebase
const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Use IndexedDB persistence — far more reliable than localStorage in iOS PWA
// (WKWebView silently purges localStorage between cold launches).
// initializeAuth throws if called twice (HMR), so we fall back to getAuth().
function createAuth(firebaseApp: FirebaseApp) {
  try {
    return initializeAuth(firebaseApp, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    // Already initialized (hot-module reload) — return the existing instance
    return getAuth(firebaseApp);
  }
}
const auth = createAuth(app);
const db = getFirestore(app);
// Pass the bucket explicitly — prevents "No default bucket found" if storageBucket
// env var is missing at build time (fallback hardcoded as a string literal).
const _storageBucket = process.env.NEXT_PUBLIC_STORAGE_BUCKET || "breakin-bread-6rr1a.firebasestorage.app";
const storage = getStorage(app, `gs://${_storageBucket}`);

// Connect to emulators only if NEXT_PUBLIC_USE_EMULATORS is set to 'true'
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_USE_EMULATORS === 'true') {
    try {
        const { connectFirestoreEmulator } = require("firebase/firestore");
        const { connectAuthEmulator } = require("firebase/auth");
        const { connectStorageEmulator } = require("firebase/storage");

        // Only connect once
        if (!(db as any)._settingsFrozen) {
            console.log("🔧 Connecting to Firebase Emulators");
            connectFirestoreEmulator(db, "127.0.0.1", 8080);
            connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
            connectStorageEmulator(storage, "127.0.0.1", 9199);
        }
    } catch (error) {
        console.warn("Firebase emulators connection error:", error);
    }
}

// Auth Providers
const googleProvider = new GoogleAuthProvider();
// Always show Google account chooser — prevents silent auto-login with remembered account
// and lets users switch between multiple Google accounts.
googleProvider.setCustomParameters({ prompt: 'select_account' });
const emailProvider = new EmailAuthProvider();
const facebookProvider = new FacebookAuthProvider();
const githubProvider = new GithubAuthProvider();
const twitterProvider = new TwitterAuthProvider();
const appleProvider = new OAuthProvider('apple.com');
const microsoftProvider = new OAuthProvider('microsoft.com');
const yahooProvider = new OAuthProvider('yahoo.com');


// Export the initialized services and providers
export { app, auth, db, storage, googleProvider, emailProvider, facebookProvider, githubProvider, twitterProvider, appleProvider, microsoftProvider, yahooProvider };
